import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { AnnualDealsGoalState } from '../../utils/annualPrime.utils';

export type DealsGoalEligibilityBannerProps = {
  currentSeniority: string;
  selectedYear: string;
  dealsState: AnnualDealsGoalState;
};

const DealsGoalEligibilityBanner = ({
  currentSeniority,
  selectedYear,
  dealsState,
}: DealsGoalEligibilityBannerProps) => {
  const { t } = useTranslation(['primes']);

  if (!dealsState.isExceeded || dealsState.isMaxSeniority || dealsState.targetSeniority === null) {
    return null;
  }

  return (
    <BannerContainer>
      <BannerIcon aria-hidden="true">
        <WorkspacePremiumIcon sx={{ fontSize: 26 }} />
      </BannerIcon>

      <BannerContent>
        <TitleRow>
          <BannerTitle>{t('dealsGoalEligibilityBannerTitle')}</BannerTitle>
          <InfoTag>
            <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            {t('dealsGoalEligibilityBannerInfoTag')}
          </InfoTag>
        </TitleRow>
        <BannerMessage>
          {t('dealsGoalEligibilityBannerMessage', {
            signedDeals: dealsState.signedDeals,
            year: selectedYear,
            targetSeniority: dealsState.targetSeniority,
            targetThreshold: dealsState.targetThreshold,
          })}
        </BannerMessage>
      </BannerContent>

      <SeniorityTransition>
        <CurrentSeniority>{currentSeniority}</CurrentSeniority>
        <TransitionArrow aria-hidden="true" />
        <NextSeniorityBadge>
          <WorkspacePremiumIcon sx={{ fontSize: 14 }} />
          {dealsState.targetSeniority}
        </NextSeniorityBadge>
      </SeniorityTransition>
    </BannerContainer>
  );
};

export default DealsGoalEligibilityBanner;

const BannerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  padding: '18px 22px',
  borderRadius: 16,
  border: `1px solid ${theme.palette.beige[80]}`,
  background: `radial-gradient(120% 180% at 0% 0%, rgba(255,255,255,.55) 0%, rgba(255,255,255,0) 55%), linear-gradient(116deg, ${theme.palette.red[20]} -6%, ${theme.palette.beige[40]} 58%, ${theme.palette.beige[60]} 120%)`,
  // body { text-align: center } in main.css — inline text would inherit it otherwise.
  textAlign: 'left',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const BannerIcon = styled('span')(({ theme }) => ({
  width: 50,
  height: 50,
  flexShrink: 0,
  borderRadius: 14,
  background: `linear-gradient(135deg, ${theme.palette.red[60]}, ${theme.palette.red[80]})`,
  color: theme.palette.neutral.white,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 5px 14px rgba(255,107,151,.4)',
}));

const BannerContent = styled('div')(() => ({
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
}));

const BannerTitle = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.neutral[90],
}));

const InfoTag = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 9px',
  borderRadius: 6,
  backgroundColor: 'rgba(255,255,255,.7)',
  color: theme.palette.neutral[60],
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: 11,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
}));

const BannerMessage = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: 13.5,
  color: theme.palette.neutral[70],
}));

const SeniorityTransition = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    flexWrap: 'wrap',
  },
}));

const CurrentSeniority = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: 13,
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));

const TransitionArrow = styled(ArrowForwardIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.neutral[50],
}));

const NextSeniorityBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  borderRadius: 999,
  backgroundColor: theme.palette.purple[100],
  color: theme.palette.neutral.white,
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 13.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
}));
